/**
 * Une échéance UNIQUE pour toutes les attentes d'un gestionnaire de trame.
 *
 * Pourquoi : un gestionnaire qui enchaîne plusieurs attentes, chacune avec sa
 * propre borne, a pour pire cas la SOMME de ces bornes — qui dépasse sans bruit
 * la borne du demandeur backend. Celui-ci rend alors « issue inconnue », puis
 * l'accusé tardif du navigateur, même `ok:true`, n'est lu par personne
 * (`open_canvas_document`, 2026-10-09 : 14 s + 14 s + un enregistrement réseau
 * non borné + 14 s + 1,5 s, face à 30 s).
 *
 * Le gestionnaire ouvre donc UNE échéance, passe chaque attente par `avant`, et
 * donne à celles qui prennent un délai ce qui reste (`resteMs`). À l'échéance,
 * l'attente en cours rejette avec le nom de son étape : c'est la vraie cause,
 * que l'accusé d'échec répète à l'agent.
 */

/** L'échéance d'un gestionnaire est tombée pendant `etape`. */
export class EcheanceDepasseeError extends Error {
  constructor(
    /** Ce que le gestionnaire attendait, en anglais : le texte part au modèle. */
    readonly etape: string,
    readonly budgetMs: number,
    /** L'échec propre de l'étape, quand il est tombé au même instant. */
    cause?: unknown,
  ) {
    super(
      `the browser-side deadline of ${budgetMs} ms was reached while ${etape}`
      + (cause === undefined ? '' : ` (${cause instanceof Error ? cause.message : String(cause)})`),
    );
    this.name = 'EcheanceDepasseeError';
  }
}

export interface EcheanceUnique {
  /** Avorté à l'échéance — à remettre aux opérations qui savent s'abandonner. */
  readonly signal: AbortSignal;
  /** Ce qui reste du budget, en millisecondes ; jamais négatif. */
  resteMs(): number;
  /**
   * Attend `pending`, ou rejette `EcheanceDepasseeError(etape)` à l'échéance.
   *
   * `pending` n'est pas annulée : une attente perdue continue en arrière-plan,
   * et son issue tardive est absorbée. L'appelant ne doit donc rien faire
   * d'irréversible APRÈS une attente passée par ici sans avoir reçu sa valeur.
   */
  avant<T>(etape: string, pending: Promise<T>): Promise<T>;
  /** Désarme la minuterie. À appeler quand le gestionnaire a répondu. */
  clore(): void;
}

/**
 * Ouvre une échéance de `budgetMs` à partir de maintenant.
 *
 * @param budgetMs Budget total du gestionnaire, strictement inférieur à la borne
 *   d'attente du demandeur.
 */
export function ouvrirUneEcheance(budgetMs: number): EcheanceUnique {
  const controleur = new AbortController();
  const ouverteA = performance.now();
  const minuterie = setTimeout(() => controleur.abort(), budgetMs);
  const resteMs = (): number => Math.max(0, budgetMs - (performance.now() - ouverteA));

  return {
    signal: controleur.signal,
    resteMs,
    avant<T>(etape: string, pending: Promise<T>): Promise<T> {
      return new Promise<T>((resolve, reject) => {
        const expirer = (): void => reject(new EcheanceDepasseeError(etape, budgetMs));
        if (controleur.signal.aborted) {
          // L'issue tardive de `pending` n'a plus de lecteur : l'absorber évite
          // un rejet non capturé.
          pending.catch(() => undefined);
          expirer();
          return;
        }
        controleur.signal.addEventListener('abort', expirer, { once: true });
        pending.then((valeur) => {
          controleur.signal.removeEventListener('abort', expirer);
          resolve(valeur);
        }, (erreur: unknown) => {
          controleur.signal.removeEventListener('abort', expirer);
          // Une attente bornée à `resteMs()` échoue au même instant que
          // l'échéance : sans cette conversion, l'ordre de deux minuteries
          // déciderait du message rendu à l'agent.
          reject(resteMs() <= 0 ? new EcheanceDepasseeError(etape, budgetMs, erreur) : erreur);
        });
      });
    },
    clore(): void {
      clearTimeout(minuterie);
    },
  };
}
